import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { permitService } from '../../../services/permitService';
import { projectService } from '../../../services/projectService';
import { useBottomSheet } from '../../../hooks/useBottomSheet';

const EditPermitModal = ({ isOpen, onClose, onSuccess, permit }) => {
  const [formData, setFormData] = useState({
    projectId: '',
    projectName: '',
    permitType: 'Building Permit',
    applicationNumber: '',
    status: 'submission',
    submittedDate: '',
    deadline: '',
    inspectorName: '',
    inspectorPhone: '',
    inspectorEmail: '',
    notes: ''
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');

  const {
    sheetStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    scrollRef,
    showTopShadow,
    showBottomShadow,
    recheckShadows,
  } = useBottomSheet(onClose);

  useEffect(() => {
    if (isOpen) {
      loadProjects();
      setError('');
      if (permit) {
        setFormData({
          projectId: permit?.projectId || '',
          projectName: permit?.projectName || '',
          permitType: permit?.permitType || 'Building Permit',
          applicationNumber: permit?.applicationNumber || '',
          status: permit?.status || 'submission',
          submittedDate: permit?.submittedDate
            ? new Date(permit?.submittedDate)?.toISOString()?.split('T')?.[0]
            : '',
          deadline: permit?.deadline
            ? new Date(permit?.deadline)?.toISOString()?.split('T')?.[0]
            : '',
          inspectorName: permit?.inspectorName || '',
          inspectorPhone: permit?.inspectorPhone || '',
          inspectorEmail: permit?.inspectorEmail || '',
          notes: permit?.notes || ''
        });
      }
    }
  }, [isOpen, permit]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const data = await projectService?.getAll();
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setLoadingProjects(false);
      recheckShadows();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'projectId' && value) {
      const selectedProject = projects?.find(p => p?.id === value);
      if (selectedProject) {
        setFormData(prev => ({
          ...prev,
          projectId: value,
          projectName: selectedProject?.customerName || selectedProject?.address || ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.projectName?.trim()) {
      setError('Project name is required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await permitService?.update(permit?.id, {
        projectId: formData?.projectId || null,
        projectName: formData?.projectName,
        permitType: formData?.permitType,
        applicationNumber: formData?.applicationNumber,
        status: formData?.status,
        submittedDate: formData?.submittedDate ? new Date(formData?.submittedDate)?.toISOString() : null,
        deadline: formData?.deadline ? new Date(formData?.deadline)?.toISOString() : null,
        inspectorName: formData?.inspectorName || null,
        inspectorPhone: formData?.inspectorPhone || null,
        inspectorEmail: formData?.inspectorEmail || null,
        notes: formData?.notes || null,
        updatedAt: new Date()?.toISOString()
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to update permit');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <div
        className="bg-card w-full sm:rounded-xl sm:max-w-2xl shadow-2xl rounded-t-2xl max-h-[92vh] flex flex-col"
        style={{
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
          ...(typeof window !== 'undefined' && window.innerWidth < 640 ? sheetStyle : {})
        }}
      >
        {/* Enhanced drag handle on mobile */}
        <div
          className="sm:hidden flex-shrink-0 flex flex-col items-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          <span className="text-[10px] text-muted-foreground/40 mt-1 font-medium tracking-wide">DRAG TO CLOSE</span>
        </div>

        {/* Sticky header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0 bg-card">
          <h2 className="text-lg font-semibold text-foreground">Edit Permit</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={22} />
          </button>
        </div>

        {/* Scroll indicator - top */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showTopShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to bottom, hsl(var(--card)) 0%, transparent 100%)', marginBottom: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Scrollable form */}
        <form ref={scrollRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Project</label>
              <select
                name="projectId"
                value={formData?.projectId}
                onChange={handleChange}
                disabled={loadingProjects}
                className="flex h-11 w-full items-center justify-between rounded-lg border border-input bg-background text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select a project (optional)</option>
                {projects?.map(project => (
                  <option key={project?.id} value={project?.id}>
                    {project?.customerName} - {project?.address}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Project Name <span className="text-destructive">*</span>
              </label>
              <Input type="text" name="projectName" value={formData?.projectName} onChange={handleChange} required placeholder="Enter project name" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Permit Type <span className="text-destructive">*</span>
              </label>
              <select
                name="permitType"
                value={formData?.permitType}
                onChange={handleChange}
                required
                className="flex h-11 w-full items-center justify-between rounded-lg border border-input bg-background text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="Building Permit">Building Permit</option>
                <option value="Electrical Permit">Electrical Permit</option>
                <option value="Plumbing Permit">Plumbing Permit</option>
                <option value="Mechanical Permit">Mechanical Permit</option>
                <option value="Zoning Permit">Zoning Permit</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Application Number</label>
              <Input type="text" name="applicationNumber" value={formData?.applicationNumber} onChange={handleChange} placeholder="APP-2024-001" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Status</label>
              <select
                name="status"
                value={formData?.status}
                onChange={handleChange}
                className="flex h-11 w-full items-center justify-between rounded-lg border border-input bg-background text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="submission">Submission</option>
                <option value="under review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Submitted Date</label>
              <Input type="date" name="submittedDate" value={formData?.submittedDate} onChange={handleChange} />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1.5">Deadline</label>
              <Input type="date" name="deadline" value={formData?.deadline} onChange={handleChange} />
            </div>
          </div>

          {/* Inspector Section */}
          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="User" size={15} />
              Inspector Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Inspector Name</label>
                <Input type="text" name="inspectorName" value={formData?.inspectorName} onChange={handleChange} placeholder="John Smith" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Inspector Phone</label>
                <Input type="tel" name="inspectorPhone" value={formData?.inspectorPhone} onChange={handleChange} placeholder="(555) 000-0000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Inspector Email</label>
                <Input type="email" name="inspectorEmail" value={formData?.inspectorEmail} onChange={handleChange} placeholder="inspector@city.gov" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Notes</label>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none"
              placeholder="Additional notes about this permit..."
            />
          </div>
        </form>

        {/* Scroll indicator - bottom */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showBottomShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to top, hsl(var(--card)) 0%, transparent 100%)', marginTop: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Sticky footer */}
        <div className="flex gap-3 px-5 pt-3 border-t border-border flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1 h-12">
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="flex-1 h-12" onClick={handleSubmit}>
            {loading ? 'Updating...' : 'Update Permit'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditPermitModal;